import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRef, useEffect, useState, forwardRef } from 'react';
import { mergeProps, useButton, useTextField, useHover, usePress, useFocusRing } from 'react-aria';

import { mergeRefs } from '../../../lib/mergeRefs';
import { ToastManager } from '../../../ToastManager.jsx';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useVariable } from '../../../store/logic/useVariables.ts';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';

import { su } from '../../../theme/functions/su';
import { input } from '../../../theme/Global';
import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { Glyph } from '../../../components/common/Glyph';
import { RoundIconWrapper } from '../../../components/common/RoundIconWrapper';
import { promptMessage } from '../../../components/overlay/DialogPrompt';

import { BooleanSelect } from './BooleanSelect';

const iconMap = {
  boolean: {
    source: glyph.logic.boolean,
    color: theme.color.logic_boolean,
  },
  number: {
    source: glyph.logic.number,
    color: theme.color.logic_number,
  },
  string: {
    source: glyph.logic.string,
    color: theme.color.logic_string,
  },
};

export function VariablesListItem({ variableId }) {
  const { i18n } = useI18n();
  const { api } = useApi();
  const { variable } = useVariable({ variableId });

  const nameRef = useRef();
  const valueRef = useRef();
  const buttonRef = useRef();
  const skipNextNameBlurSaveRef = useRef(false);
  const skipNextValueBlurSaveRef = useRef(false);
  const [isEditName, setIsEditName] = useState(false);
  const [isEditValue, setIsEditValue] = useState(false);

  const hover = useHover({});
  const button = useButton(
    {
      onPress: () => {
        promptMessage({
          type: 'danger',
          message: i18n.messageFormatter('logic.removeVariablePrompt', {
            name: variable.name,
          }),
        })
          .then(() => {
            api.logic.deleteVariable({
              id: variable?.id,
            });
          })
          .catch(ToastManager.handleError);
      },
    },
    buttonRef,
  );

  const focusRing = useFocusRing({});

  if (variable == null) return null;

  function onRequestSaveName({ name }) {
    // TODO
    // use draft store here
    api.logic
      .updateVariable({
        id: variable.id,
        variable: {
          name,
        },
      })
      .catch(ToastManager.handleError);
  }

  function onRequestSaveValue({ value }) {
    // TODO
    // use draft store here
    api.logic
      .updateVariable({
        id: variable.id,
        variable: {
          value: inputType === 'number' ? Number(value) : value,
        },
      })
      .catch(ToastManager.handleError);
  }

  const inputType = (() => {
    switch (variable.type) {
      case 'string':
        return 'text';
      case 'number':
        return 'number';
      case 'boolean':
        return 'select';
      default:
        return 'text';
    }
  })();

  const isDeleteIconVisible = hover.isHovered || focusRing.isFocusVisible;

  function focusInput(inputRef) {
    inputRef.current?.focus();
  }

  return (
    <sc.ListItem>
      <sc.VariableButton
        {...mergeProps(hover.hoverProps, focusRing.focusProps, button.buttonProps)}
        ref={buttonRef}
      >
        <RoundIconWrapper
          size={theme.icon.size_medium}
          color={isDeleteIconVisible ? theme.color.danger : iconMap[variable.type].color}
        >
          <Glyph
            size={isDeleteIconVisible ? theme.icon.size_tiny : theme.icon.size_small}
            source={isDeleteIconVisible ? glyph.core.cross : iconMap[variable.type].source}
            color={theme.color.icon_white}
          />
        </RoundIconWrapper>
      </sc.VariableButton>

      <TextField
        ref={nameRef}
        type="text"
        align="start"
        aria-label="Name"
        placeholder={i18n.messageFormatter(`logic.namePlaceholder`)}
        defaultValue={variable.name}
        isReadOnly={isEditName === false}
        onFocus={() => {
          setIsEditName(true);
        }}
        onPress={() => {
          setIsEditName(true);
          focusInput(nameRef);
        }}
        onBlur={(event) => {
          if (skipNextNameBlurSaveRef.current === true) {
            skipNextNameBlurSaveRef.current = false;
            return;
          }

          const value = event.target.value;
          setIsEditName(false);

          onRequestSaveName({
            name: value,
          });
        }}
        onKeyDown={(event) => {
          const key = event.key;
          switch (key) {
            case 'Tab':
              if (event.shiftKey === true) {
                break;
              }

              event.preventDefault();
              setIsEditValue(true);
              focusInput(valueRef);
              break;
            default:
              break;
          }
        }}
        onKeyUp={(event) => {
          const key = event.key;
          switch (key) {
            case 'Enter': {
              const value = event.target.value;

              onRequestSaveName({
                name: value,
              });

              break;
            }
            case 'Escape':
              setIsEditName(false);
              skipNextNameBlurSaveRef.current = true;
              event.currentTarget.blur();
              break;
            default:
              break;
          }
        }}
      />

      {inputType === 'select' ? (
        <BooleanSelect
          inputRef={valueRef}
          align="right"
          aria-label="Value"
          defaultValue={variable.value}
          onChange={(value) => {
            onRequestSaveValue({
              inputType: inputType,
              value: value,
            });
          }}
        />
      ) : (
        <TextField
          ref={valueRef}
          type={inputType}
          align="end"
          aria-label="Value"
          placeholder={i18n.messageFormatter(`logic.${variable.type}Placeholder`)}
          isReadOnly={isEditValue === false}
          defaultValue={variable.value}
          onFocus={() => {
            setIsEditValue(true);
          }}
          onPress={() => {
            setIsEditValue(true);
            focusInput(valueRef);
          }}
          onBlur={(event) => {
            if (skipNextValueBlurSaveRef.current === true) {
              skipNextValueBlurSaveRef.current = false;
              return;
            }

            const value = event.target.value;
            setIsEditValue(false);

            onRequestSaveValue({
              inputType: inputType,
              value: value,
            });
          }}
          onKeyDown={(event) => {
            const key = event.key;
            switch (key) {
              case 'Tab':
                if (event.shiftKey === false) {
                  break;
                }

                event.preventDefault();
                setIsEditName(true);
                focusInput(nameRef);
                break;
              default:
                break;
            }
          }}
          onKeyUp={(event) => {
            const key = event.key;

            switch (key) {
              case 'Enter': {
                const value = event.target.value;

                onRequestSaveValue({
                  inputType: inputType,
                  value: value,
                });
                break;
              }
              case 'Escape':
                setIsEditValue(false);
                skipNextValueBlurSaveRef.current = true;
                event.currentTarget.blur();
                break;
              default:
                break;
            }
          }}
        />
      )}
    </sc.ListItem>
  );
}

const TextField = forwardRef(function (props, forwardedRef) {
  const ref = useRef();
  const defaultValue = props.defaultValue == null ? '' : String(props.defaultValue);
  const [value, setValue] = useState(defaultValue);
  const textField = useTextField(
    {
      ...props,
      value,
      onChange: setValue,
    },
    ref,
  );
  const press = usePress({
    onPress: props.onPress,
  });

  useEffect(() => {
    if (props.isReadOnly === true) {
      setValue(defaultValue);
    }
  }, [defaultValue, props.isReadOnly]);

  return (
    <sc.Input
      {...mergeProps(textField.inputProps, props.isReadOnly ? press.pressProps : {})}
      aria-label={props['aria-label']}
      title={defaultValue}
      align={props.align}
      isReadOnly={props.isReadOnly}
      justifySelf={props.justifySelf}
      ref={mergeRefs([ref, forwardedRef])}
      step={props.type === 'number' ? 'any' : undefined}
    />
  );
});

const VariableButton = styled.button`
  //cursor: pointer;
`;

const ListItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  grid-auto-rows: ${su(5)};
  grid-gap: ${su(1)};

  align-items: center;
  outline: 0;
  padding: 0 ${su(2)};
  line-height: 20px;

  &:hover {
    background-color: ${theme.color.background_hover};
  }
`;

const Input = styled.input`
  ${input};
  min-width: 0;
  background-color: ${theme.input.background};
  border: 1px solid ${theme.input.border};
  outline: 0;
  border-radius: ${theme.borderRadius.default};
  line-height: 30px;

  text-align: ${(props) => props.align};
  padding: 0 5px;
  margin-${(props) => props.align}: -6px;

  &:hover {
    border-color: ${theme.input.border};
    background: ${theme.color.component};
  }

  ${(props) =>
    props.isReadOnly &&
    css`
      background-color: transparent;
      border-color: transparent;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        background-color: ${theme.color.background_hover};
      }
    `};
`;

const sc = {
  ListItem,
  VariableButton,
  Input,
};
